import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/use-toast'
import { EstateForSalePageFragment, useUpdateEstateMutation } from '@gql'
import debounce from 'lodash.debounce'
import { CheckIcon, InfoIcon, SettingsIcon } from 'lucide-react'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EstateSettingLinkTab } from './EstateSettingLinkTab'
import { EstateSettingCurrentRoundTab } from './EstateSettingCurrentRoundTab'
import { EstateSettingOpeningTab } from './EstateSettingOpeningTab'
import { EstateSettingVisitSpanTab } from './EstateSettingVisitSpanTab'

type EstateSettingModalProps = {
  estate: Pick<
    EstateForSalePageFragment,
    | 'id'
    | 'isPublished'
    | 'seller'
    | 'firstPrice'
    | 'reservePrice'
    | 'estate_links'
    | 'visit_spans'
    | 'estateRounds'
    | 'currentRoundId'
    | 'isSaleOpen'
    | 'visitSlotDuration'
    | 'maxVisitPerSlot'
  >
  disabled?: boolean
  readOnly?: boolean
}

export default function EstateSettingModal({
  estate,
  disabled,
  readOnly,
}: EstateSettingModalProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  const [confirmationPendingCallback, setConfirmationPendingCallback] =
    useState<() => Promise<void> | void>()

  const [updateEstate] = useUpdateEstateMutation({
    refetchQueries: ['getEstates'],
  })
  const debounceUpdateEstate = debounce(updateEstate, 500)

  const [value, setValue] = useState('opening')
  const isOnSale = estate.isSaleOpen && estate.isPublished

  const publish = useCallback(async () => {
    if (isOnSale) return
    await debounceUpdateEstate({
      variables: {
        id: estate.id,
        values: {
          isPublished: true,
          isSaleOpen: true,
        },
      },
      onCompleted: () => {
        toast({
          variant: 'success',
          description: t('EstateSettingModal.toast.success'),
        })
      },
      onError: (error) => {
        console.error(error)
        toast({
          variant: 'destructive',
          description: t('EstateSettingModal.toast.error'),
        })
      },
    })
  }, [debounceUpdateEstate, estate.id, isOnSale, t, toast])

  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <Button disabled={disabled}>
            <SettingsIcon className="tw-h-4 tw-w-4 tw-mr-2" />
            {t('EstateSettingModal.button')}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader className="tw-mb-5">
            <DialogTitle>{t('EstateSettingModal.heading')}</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          <Tabs value={value} onValueChange={setValue}>
            <TabsList className="tw-w-full">
              <TabsTrigger className="tw-w-full" value="opening">
                {t('EstateSettingModal.tabs.opening')}
              </TabsTrigger>
              <TabsTrigger className="tw-w-full" value="visits">
                {t('EstateSettingModal.tabs.visits')}
              </TabsTrigger>
              <TabsTrigger className="tw-w-full" value="newRound">
                {t('EstateSettingModal.tabs.newRound')}
              </TabsTrigger>
              <TabsTrigger className="tw-w-full" value="publication">
                {t('EstateSettingModal.tabs.publication')}
              </TabsTrigger>
            </TabsList>
            <TabsContent
              forceMount={true} // Required to have a smooth experience on controlled component on NextJS
              className={`tw-h-[650px] tw-overflow-auto tw-p-1 ${value === 'opening' ? '' : 'tw-hidden'}`} // Required to have a smooth experience on controlled component on NextJS
              value="opening"
            >
              {readOnly && <EstateSettingModalReadOnlyCard estate={estate} />}
              <EstateSettingOpeningTab estate={estate} readOnly={readOnly} />
            </TabsContent>
            <TabsContent
              forceMount={true} // Required to have a smooth experience on controlled component on NextJS
              className={`tw-h-[650px] tw-overflow-auto tw-p-1 ${value === 'visits' ? '' : 'tw-hidden'}`} // Required to have a smooth experience on controlled component on NextJS
              value="visits"
            >
              {readOnly && <EstateSettingModalReadOnlyCard estate={estate} />}
              <EstateSettingVisitSpanTab estate={estate} readOnly={readOnly} />
            </TabsContent>
            <TabsContent
              forceMount={true} // Required to have a smooth experience on controlled component on NextJS
              className={`tw-h-[650px] tw-overflow-auto tw-p-1 ${value === 'newRound' ? '' : 'tw-hidden'}`} // Required to have a smooth experience on controlled component on NextJS
              value="newRound"
            >
              {readOnly && <EstateSettingModalReadOnlyCard estate={estate} />}
              <EstateSettingCurrentRoundTab
                estate={estate}
                readOnly={readOnly}
              />
            </TabsContent>
            <TabsContent
              forceMount={true} // Required to have a smooth experience on controlled component on NextJS
              className={`tw-h-[650px] tw-overflow-auto tw-p-1 ${value === 'publication' ? '' : 'tw-hidden'}`} // Required to have a smooth experience on controlled component on NextJS
              value="publication"
            >
              {readOnly && <EstateSettingModalReadOnlyCard estate={estate} />}
              <EstateSettingLinkTab estate={estate} readOnly={readOnly} />
            </TabsContent>
          </Tabs>
          <DialogFooter>
            {!readOnly && (
              <Button
                variant="secondary"
                disabled={isOnSale}
                onClick={() =>
                  setConfirmationPendingCallback(() => () => publish())
                }
              >
                {isOnSale && <CheckIcon className="tw-w-4 tw-h-4" />}
                {isOnSale
                  ? t('EstateSettingModal.published')
                  : t('EstateSettingModal.publish')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={!!confirmationPendingCallback}
        onOpenChange={() => setConfirmationPendingCallback(undefined)}
        onConfirm={confirmationPendingCallback}
      />
    </>
  )
}

const EstateSettingModalReadOnlyCard = ({
  estate,
}: {
  estate: Pick<EstateForSalePageFragment, 'id' | 'seller'>
}) => {
  const { t } = useTranslation()

  return (
    <div className="tw-py-3 tw-p-4 tw-bg-green-200 tw-rounded-lg tw-flex tw-flex-row tw-gap-2 tw-text-green-700 tw-items-center">
      <InfoIcon className="tw-h-[32px] tw-w-[32px]" />
      <p className="tw-text-sm tw-font-normal tw-flex tw-items-center">
        {t('EstateSettingModal.readOnly.title', {
          agency: estate.seller.agency.name,
        })}
      </p>
    </div>
  )
}
